import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Co-founder / builder sites open in Snag Builder, not App Builder. */
function snagBuilderPath(appId) {
  const n = Number(appId);
  if (Number.isFinite(n) && n > 0) return `/snagbuilder?appId=${n}`;
  return '/snagbuilder';
}

const WebsitePreview = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    fetchWebsite();
  }, [websiteId]);

  const fetchWebsite = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/websites/${websiteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        // SiteBrain project ids are sometimes pasted into /preview/:id —
        // Co-founder defaults to Snag Builder (SiteBrain UUID ≠ numeric appId).
        const projectRes = await fetch(`${API_URL}/api/sitebrain/projects/${websiteId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (projectRes.ok) {
          setRedirecting(true);
          navigate(snagBuilderPath(), { replace: true });
          return;
        }
        throw new Error('Website not found');
      }

      const data = await response.json();
      const html = (data.html_code || '').trim();
      const projectId = (data.sitebrain_project_id || '').trim();
      const snagAppId = data.alifullstack_app_id || data.snag_app_id || '';
      const source = (data.source || '').trim();
      const isSnag =
        Boolean(snagAppId)
        || source === 'snag-builder'
        || source === 'alifullstack'
        || source === 'snagbuilder';
      const isAppBuilder = data.source === 'app-builder' || Boolean(projectId);

      // Builder-backed sites store content in Snag/SiteBrain, not html_code.
      if ((isSnag || (isAppBuilder && !html)) && (snagAppId || projectId || isSnag || isAppBuilder)) {
        setRedirecting(true);
        navigate(snagBuilderPath(snagAppId), { replace: true });
        return;
      }

      setWebsite(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading || redirecting) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">
            {redirecting ? 'Opening Snag Builder…' : 'Loading preview...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <p className="text-gray-500 text-sm mb-4">
            Classic preview only works for template websites with HTML. Builder projects open in Snag Builder.
          </p>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/snagbuilder')}
              className="px-4 py-2 bg-[#fbbf0f] text-[#16130d] rounded-lg font-semibold hover:bg-[#ffd84a]"
            >
              Open Snag Builder
            </button>
            <button
              type="button"
              onClick={() => window.close()}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const fullHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${website?.name || 'Website'}</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', -apple-system, sans-serif; }
    ${website?.css_code || ''}
  </style>
</head>
<body>
  ${website?.html_code || '<p>No content</p>'}
  <script>${website?.js_code || ''}</script>
</body>
</html>`;

  return (
    <iframe
      srcDoc={fullHtml}
      className="w-full h-screen border-0"
      title="Website Preview"
    />
  );
};

export default WebsitePreview;
